import store from "@/store/index";
import request from "@/utils/request";

// ========== 状态变量 ==========

// 心跳系统：定时发送 ping，检测服务端是否存活
var heartbeatTimer = null; // ping 发送倒计时（每 timeout 毫秒发一次）
var serverTimeoutTimer = null; // 服务端响应超时倒计时（发完 ping 后等 timeout 毫秒，没收到 pong 就判定断连）

// 发送重连的回调系统：消息发送时注册 onConnected/onFailed，连接成功后触发
var pendingSendConnected = null; // 待执行的 onConnected 回调
var pendingSendConnectedFallbackTimer = null; // onConnected 的保底定时器（防止 bindUser 请求挂起导致回调永远不执行）

// 通用重连系统：所有断连场景（心跳超时、WebSocket error、消息发送失败）共用
var activeSendRetryTimer = null; // 追踪当前活跃的发送重连 timer（用于取消）
var sendRetryActive = false; // 手动重试的瞬时标记，防止 reconnect() 的自检挡住立即连接
var reconnectRetryCount = 0; // backoff 计数：0→1s, 1→2s, 2→4s, ≥3→10s
var reconnectTimer = null; // 通用重连的 timer（用于手动重试时取消当前等待）
var sendCallbacks = { onConnected: null, onFailed: null }; // 消息发送注册的回调，连接成功/第3次失败时触发

// ========== 回调执行 ==========

/**
 * 执行待处理的 onConnected 回调
 * 有保底定时器兜底：即使 bindUser 请求挂起，最多 1.5s 后也会执行回调
 */
function resolvePendingSendConnected() {
  if (pendingSendConnectedFallbackTimer) {
    clearTimeout(pendingSendConnectedFallbackTimer);
    pendingSendConnectedFallbackTimer = null;
  }
  const cb = pendingSendConnected;
  pendingSendConnected = null;
  if (typeof cb === "function") {
    cb();
  }
}

// ========== WebSocket 生命周期 ==========

/**
 * 创建 WebSocket 连接并绑定四个生命周期事件
 * 每次重连都会调用此函数创建全新的 WebSocket 实例
 */
function initWebpack() {
  var wsurl = localStorage.getItem("socketUrl");
  store.state.ws = new WebSocket(wsurl);
  store.state.ws.onopen = onopen;
  store.state.ws.onmessage = onmessage;
  store.state.ws.onclose = onclose;
  store.state.ws.onerror = onerror;
}

/**
 * 连接建立成功 → 启动心跳
 */
function onopen() {
  console.log(`连接websocket----${new Date()}`);
  sendPing();
}

/**
 * 心跳机制：每隔 timeout 毫秒发送一次 ping
 * 流程：等待 timeout → 发送 ping → 等待 timeout → 如果没收到 pong → 判定断连 → 触发 reconnect()
 * 如果在等待期间收到服务端消息（onmessage），会调用 reset() 重新计时
 */
function sendPing() {
  var self = store.state;
  clearTimeout(heartbeatTimer);
  clearTimeout(serverTimeoutTimer);
  heartbeatTimer = setTimeout(function () {
    if (self.ws.readyState === 1) {
      // WebSocket 处于 OPEN 状态，发送 ping
      self.ws.send(
        JSON.stringify({
          type: "ping",
          room_id: 1,
          config: {
            platform: "pushWeb",
          },
        })
      );
      // 发完 ping 后等 timeout 毫秒，超时则判定服务端无响应
      serverTimeoutTimer = setTimeout(function () {
        console.error("心跳超时，准备重连");
        self.ws.close();
        reconnect();
      }, self.timeout);
    } else {
      // WebSocket 已断开，直接触发重连
      reconnect();
    }
  }, self.timeout);
}

// ========== 重连核心 ==========

/**
 * 通用重连函数 —— 所有断连场景的唯一入口
 * backoff 策略：第1次 1s，第2次 2s，第3次 4s，第4次起 10s
 * 连接成功后计数归零，下次断连重新从 1s 开始
 *
 * 调用来源：
 *   1. 心跳超时（sendPing → serverTimeoutTimer → reconnect）
 *   2. WebSocket error（onerror → reconnect）
 *   3. WebSocket 未连接时发消息（reconnectForSend → reconnect）
 */
function reconnect() {
  var that = store.state;
  that.isDisconnected = true;
  // 防止重复触发：如果已有重连在进行中（timer 等待中或 WebSocket 连接中），跳过
  // sendRetryActive 为 true 时（用户手动重试），跳过 lockReconnect 检查立即连接
  if (!sendRetryActive && that.lockReconnect) {
    return;
  }
  that.lockReconnect = true;
  // 清除心跳定时器，避免心跳系统干扰重连
  clearTimeout(heartbeatTimer);
  clearTimeout(serverTimeoutTimer);
  clearTimeout(reconnectTimer);

  // 计算 backoff 延迟
  var delay;
  if (reconnectRetryCount < 3) {
    delay = Math.pow(2, reconnectRetryCount) * 1000; // 0→1s, 1→2s, 2→4s
  } else {
    delay = 10000; // 第4次起固定 10s
  }

  // 延迟后创建新 WebSocket 连接
  reconnectTimer = setTimeout(function () {
    reconnectRetryCount++;
    var wsurl = localStorage.getItem("socketUrl");
    var newWs = new WebSocket(wsurl);

    // 新连接建立成功
    newWs.onopen = function () {
      store.state.ws = newWs;
      store.state.isDisconnected = false;
      reconnectRetryCount = 0; // 连接成功，重置 backoff
      // 替换为标准处理器（onopen/onmessage/onclose/onerror）
      newWs.onmessage = onmessage;
      newWs.onclose = onclose;
      newWs.onerror = onerror;
      // 重启心跳
      reset();
      that.lockReconnect = false;
      // 如果有消息发送注册的 onConnected 回调，执行它（触发 resendPendingMessages）
      if (typeof sendCallbacks.onConnected === "function") {
        pendingSendConnected = sendCallbacks.onConnected;
        if (pendingSendConnectedFallbackTimer) {
          clearTimeout(pendingSendConnectedFallbackTimer);
        }
        // 保底定时器：bindUser 请求可能挂起，1.5s 后强制执行回调
        pendingSendConnectedFallbackTimer = setTimeout(function () {
          resolvePendingSendConnected();
        }, 1500);
        sendCallbacks.onConnected = null;
        sendCallbacks.onFailed = null;
      }
    };

    // 新连接失败
    newWs.onerror = function () {
      newWs.close();
      // 第3次失败时，执行 onFailed 回调（通知 UI 显示重发按钮）
      if (reconnectRetryCount === 3) {
        if (typeof sendCallbacks.onFailed === "function") {
          sendCallbacks.onFailed();
          sendCallbacks.onFailed = null;
        }
      }
      // 释放锁，递归调用 reconnect 继续下一轮 backoff
      that.lockReconnect = false;
      reconnect();
    };
  }, delay);
}

// ========== 心跳辅助 ==========

/**
 * 重置心跳计时器 —— 收到服务端任何消息时调用
 * 效果：重新开始计时 timeout 毫秒后发送下一次 ping
 */
function reset() {
  clearTimeout(heartbeatTimer);
  clearTimeout(serverTimeoutTimer);
  sendPing();
}

// ========== WebSocket 事件处理器 ==========

/**
 * 收到服务端消息 → 转发给页面（window.onmessageSocket）→ 重置心跳
 * 页面通过 ChatNew.vue 的 onmessage 方法处理业务消息
 */
function onmessage(e) {
  if (typeof window.onmessageSocket === "function") {
    window.onmessageSocket(e);
  }
  const redata = JSON.parse(e.data);
  // 服务端分配 client_id 后，绑定用户（HTTP 请求）
  if (redata.type == "get_client_id") {
    request
      .post("/teacher/chat/bindUser", {
        client_id: redata.data.client_id,
      })
      .then(() => {
        // 绑定成功，执行 onConnected 回调（重发待发送消息）
        resolvePendingSendConnected();
      })
      .catch(() => {});
  }
  // 每收到消息就重置心跳计时器（说明服务端还活着）
  reset();
}

/**
 * 连接关闭 —— 仅打印日志，实际重连由 onerror 或心跳超时触发
 */
function onclose(e) {
  console.log("websocket 断开: ", e);
}

/**
 * 连接出错 → 触发通用重连
 */
function onerror(e) {
  console.log("出现错误");
  reconnect();
}

// ========== 外部调用的辅助函数 ==========

/**
 * 清除心跳定时器
 * 在 reconnectForSend 创建新 WebSocket 前调用，防止心跳系统干扰
 */
function clearHeartbeatTimers() {
  clearTimeout(heartbeatTimer);
  clearTimeout(serverTimeoutTimer);
}

/**
 * 取消当前活跃的发送重连
 * 在新的 reconnectForSend 调用前或用户取消操作时调用
 */
function cancelSendRetry() {
  sendRetryActive = false;
  if (activeSendRetryTimer) {
    clearTimeout(activeSendRetryTimer);
    activeSendRetryTimer = null;
  }
}

/**
 * 消息发送失败时的重连入口 —— 注册回调后委托给通用 reconnect()
 *
 * @param {Function} onConnected  - 连接成功回调（触发 resendPendingMessages 重发待发消息）
 * @param {Function} onFailed     - 第3次失败回调（通知 UI 显示重发按钮）
 * @param {Boolean}  manualRetry  - true=用户手动点击重试（立即连接，重置 backoff）
 *                                  false=自动触发（走通用 reconnect 的 backoff 延迟）
 *
 * 两种模式的区别：
 *   manualRetry=true:  用户点击"重试"按钮 → 取消当前等待 → 立即发起连接
 *   manualRetry=false: 消息发送发现断连 → 注册回调 → 走通用 reconnect 的自动 backoff
 */
function reconnectForSend(onConnected, onFailed, manualRetry) {
  cancelSendRetry();
  sendCallbacks.onConnected = onConnected;
  sendCallbacks.onFailed = onFailed;

  if (manualRetry) {
    // 手动重试：取消当前等待，重置 backoff，立即连接
    clearTimeout(reconnectTimer);
    store.state.lockReconnect = false;
    reconnectRetryCount = 0;
    // sendRetryActive 瞬时为 true，让 reconnect() 跳过 lockReconnect 检查
    sendRetryActive = true;
    reconnect();
    sendRetryActive = false;
  } else {
    // 自动重试：直接调通用 reconnect，共享同一套 backoff
    reconnect();
  }
}

// ========== 导出 ==========

export default {
  initWebpack,
  reconnectForSend,
  cancelSendRetry,
  clearHeartbeatTimers,
  onmessage,
  onclose,
  onopen,
  onerror,
};
